import { useState, useEffect, useCallback, useMemo } from "react";
import { usePost } from "../../API";
import {
  Button,
  useToast,
  DataTable,
  Modal,
  Badge,
  Spinner,
  Checkbox,
  Card,
  CardBody,
  Icon,
  Loading,
  Page
} from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import moment from "moment";
import { DocumentUpload } from "./DocumentUpload";
import { ColumnDef } from "@tanstack/react-table";
import { forceInitiateFileDownload } from "../../Util";
import { DocumentLibraryItem } from "@aidkitorg/types/lib/convoai/documentLibrary.types";

type DocumentPreview = {
  id: string;
  filename: string;
  url: string;
}

export function DocumentLibrary() {

  const [documents, setDocuments] = useState<DocumentLibraryItem[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentLibraryItem[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [filterByDocType, setFilterByDocType] = useState<string[]>([]);
  const [availableDocTypes, setAvailableDocTypes] = useState<{ type: string, count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewDocument, setPreviewDocument] = useState<DocumentLibraryItem | null>(null);

  const getAllDocumentsRequest = usePost('/ai/document_library/get_all');
  const downloadDocumentRequest = usePost('/ai/document_library/download');
  const deleteDocumentRequest = usePost('/ai/document_library/delete');

  const { toast } = useToast();

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllDocumentsRequest({});
      if (response.success && response.data) {
        setDocuments(response.data);

        const docTypeCountMap = response.data.reduce((counts, doc) => ({
          ...counts,
          [doc.category!]: (counts[doc.category!] || 0) + 1
        }), {} as Record<string, number>);

        setAvailableDocTypes(Object.entries(docTypeCountMap).map(([type, count]) => ({ type, count })));
      } else {
        toast({ description: `Failed to load documents: ${response.error || 'Unknown error'}`, variant: 'error' });
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({ description: 'Failed to load documents', variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [getAllDocumentsRequest, toast]);

  useAsyncEffect(loadDocuments, []);

  useEffect(() => {
    if (filterByDocType.length === 0) {
      setFilteredDocuments(documents);
    } else {
      setFilteredDocuments(documents.filter(doc => doc.category && filterByDocType.includes(doc.category)));
    }
  }, [documents, filterByDocType]);

  function handleDocumentSelect(docId: string, selected: boolean) {
    setSelectedDocuments(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(docId);
      } else {
        newSelection.delete(docId);
      }
      return newSelection;
    });
  }

  async function handleDownload(doc: DocumentLibraryItem) {
    try {
      const response = await downloadDocumentRequest({ id: doc.id.toString() });
      if (response.success && response.data) {
        const fileResponse = await fetch(response.data.url);
        if (!fileResponse.ok) {
          throw new Error('Failed to fetch file');
        }

        const blob = await fileResponse.blob();
        forceInitiateFileDownload(blob, doc.original_filename!);

      } else {
        toast({ description: `Failed to download document: ${response.error || 'Unknown error'}`, variant: 'error' });
      }
    } catch (error) {
      toast({ description: 'Failed to download document', variant: 'error' });
    }
  }

  async function handleDelete(item: DocumentLibraryItem) {
    if (confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      try {
        const response = await deleteDocumentRequest({ id: item.id });
        if (response.success) {
          setDocuments(prev => prev.filter(doc => doc.id !== item.id));
          setSelectedDocuments(prev => {
            const newSelection = new Set(prev);
            newSelection.delete(item.id);
            return newSelection;
          });
          toast({ description: 'Document deleted successfully', variant: 'success' });
        } else {
          toast({ description: `Failed to delete document: ${response.error || 'Unknown error'}`, variant: 'error' });
        }
      } catch (error) {
        console.error('Error deleting document:', error);
        toast({ description: 'Failed to delete document', variant: 'error' });
      }
    }
  }

  const allSelected = useMemo(() =>
    filteredDocuments.length > 0 && filteredDocuments.every(doc => selectedDocuments.has(doc.id))
  , [filteredDocuments, selectedDocuments]);

  const someSelected = useMemo(() =>
    filteredDocuments.some(doc => selectedDocuments.has(doc.id))
  , [filteredDocuments, selectedDocuments]);

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
        <Spinner size="large" />
      </div>
    );
  }

  const tableColumns: ColumnDef<DocumentLibraryItem>[] = [
    {
      id: 'select',
      // TODO(luke): figure out how to get DataTable to 
      // allow buttons in header. right now it disables them, 
      // making using checkboxes impossible. 
      header: () => (
        <Checkbox
          label=""
          checked={allSelected}
          onChange={selectAll => {
            if (!!selectAll) {
              setSelectedDocuments(new Set(filteredDocuments.map(doc => doc.id)));
            } else {
              setSelectedDocuments(new Set());
            }
          }}
          aria-label={`Select all documents`}
        />
      ),
      cell: ({ row: { original: { id, original_filename } } }) => (
        <Checkbox
          label=""
          checked={selectedDocuments.has(id)}
          onChange={e => handleDocumentSelect(id, !!e)}
          aria-label={`Select document ${original_filename}`}
        />
      )
    },
    {
      id: 'fileName',
      header: 'Doc Name',
      accessorKey: 'original_filename'
    },
    {
      accessorKey: 'created_at',
      id: 'created_at',
      header: 'Created On',
      cell: ({ row }) => moment(row.original.created_at).format('MM/DD/YYYY HH:mm A'),
    },
    {
      accessorKey: 'category',
      id: 'category',
      header: 'Doc Type',
      cell: ({ row }) => <Badge className="text-sm" variant="info">{row.original.category || 'N/A'}</Badge>,
    },
    {
      accessorKey: 'valid',
      id: 'valid',
      header: 'Valid',
      cell: ({ row: { original: { valid } } }) => (
        <Badge className="text-sm" variant={valid ? 'neutral' : 'error'}>
          {valid ? 'Valid' : 'Invalid'}
        </Badge>
      ),
    },
    {
      id: 'variations',
      header: 'Variations',
      minSize: 250,
      cell: ({ row: { original: { metadata } } }) => {
        const variations: string[] = metadata?.variations ?? [];
        if (variations.length) {
          return (
            <span className="text-sm text-gray-500">
              {variations.join(', ')}
            </span>
          )
        } else {
          return (
            <span className="text-sm text-gray-300">
              No Variations
            </span>
          )
        }
      },
    },
    {
      id: 'template',
      accessorKey: 'template',
      header: 'Templated',
      cell: ({ row: { original: { template } } }) => (
        <Icon name={template ? 'CheckBadgeIcon' : 'NoSymbolIcon'} />
      )
    }
  ];

  return <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Document Library</h2>
        <div className="flex gap-3 items-center">
          {selectedDocuments.size > 0 && (
            <Badge variant="neutral">
              {selectedDocuments.size} document{selectedDocuments.size === 1 ? '' : 's'} selected
            </Badge>
          )}
          <DocumentUpload
            onUploadComplete={loadDocuments}
            categories={availableDocTypes.map(t => t.type)}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Icon name="FunnelIcon" />
        {availableDocTypes.map(({ type, count }) => (
          <Checkbox
            label={`${type} (${count})`}
            key={type}
            id={`doctype-${type}`}
            checked={filterByDocType.includes(type)}
            onChange={(e) => {
              if (!!e) {
                if (!filterByDocType.includes(type)) {
                  setFilterByDocType(filterByDocType.concat(type));
                }
              } else {
                setFilterByDocType(filterByDocType.filter(docType => type !== docType));
              }
            }}
          />
        ))}
      </div>

      <Card>
        <CardBody>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                label="Select All"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={selectAll => {
                  if (!!selectAll) {
                    setSelectedDocuments(new Set(filteredDocuments.map(doc => doc.id)));
                  } else {
                    setSelectedDocuments(new Set());
                  }
                }}
              />
            </div>
            <p className="text-sm text-gray-500">
              Showing {filteredDocuments.length} of {documents.length} documents
            </p>
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No documents found.</p>
            </div>
          ) : (
            <DataTable
              data={filteredDocuments}
              actions={[
                {
                  label: 'Preview',
                  onClick(item) {
                    setPreviewDocument(item);
                  }
                },
                {
                  label: 'Download',
                  onClick: handleDownload
                },
                {
                  label: 'Delete',
                  onClick: handleDelete
                }
              ]}
              resizableColumns
              columns={tableColumns}
              pageSize={50}
            />
          )}
        </CardBody>
      </Card>
    </div>

    {/* Preview Modal */}
    {previewDocument && <Modal
      open={!!previewDocument}
      onClose={() => setPreviewDocument(null)}
      title={`Document Preview - ${previewDocument?.original_filename}`}
      size="lg"
    >
      <PreviewDocument
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </Modal>
    }
  </div>
}

export function PreviewDocument({ document, onClose }: { document: DocumentLibraryItem, onClose(): void }) {

  const downloadDocumentRequest = usePost('/ai/document_library/download');
  const [previewDocument, setPreviewDocument] = useState<DocumentPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useAsyncEffect(async () => {
    try {
      const response = await downloadDocumentRequest({ id: document.id.toString() });
      if (response.success && response.data) {
        setPreviewDocument({
          id: document.id,
          filename: document.original_filename || `Document ${document.id.toString()}`,
          url: response.data.url
        });
      } else {
        setError(`Failed to generate preview: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      setError('Failed to generate preview');
    }
  }, [downloadDocumentRequest, document]);

  if (error) {
    return <span>{error}</span>;
  }

  if (!previewDocument) {
    return <Loading />;
  }

  return <div className="space-y-4">
    <div className="space-y-4">
      <div>
        <embed
          src={previewDocument.url}
          width="100%"
          height="500px"
          title={`Document Preview - ${document.original_filename}`}
          className="border border-gray-300 rounded"
        />
      </div>
      <ul className="">
        <li><span className="text-sm"><strong>Document:</strong>&nbsp;{document.original_filename}</span></li>
        <li><span className="text-sm"><strong>Type:</strong>&nbsp;{document.category}</span></li>
        <li><span className="text-sm"><strong>Created On:</strong>&nbsp;{moment(document.created_at).toLocaleString()}</span></li>
        <li><span className="text-sm"><strong>Attributes:</strong>&nbsp;{document.metadata?.variations?.join(', ') || 'None'}</span></li>
      </ul>
    </div>

    <div className="flex justify-end space-x-3 pt-4 border-t">
      <Button variant="secondary" onClick={() => onClose()}>
        Close
      </Button>
      <Button variant="primary" onClick={() => window.open(previewDocument.url, '_blank')}>
        Open in New Tab
      </Button>
    </div>
  </div>
}
